import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectRouteParams } from 'src/app/app.selectors';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { addHardphoneSuccess } from 'src/app/pages/add-phone-to-device/state/add-hardphone.actions';
import { AddPhoneComponent } from 'src/app/pages/add-phone/containers/add-phone.component';
import { ExtensionHackedFraudDetails } from 'src/app/pages/extension-details/helpers/hacked-fraud-helper';
import { ExtensionDetailApiService } from 'src/app/pages/extension-details/services/extension-detail-api.service';
import { getHackedFraudDetails } from 'src/app/pages/extension-details/state/extension-detail.selectors';
import * as AddPhoneActions from '../../../add-phone/state/add-phone.actions';
import * as ExtensionDetailActions from '../../../extension-details/state/extension-detail.actions';
import * as PresenceContactRegisterActions from '../../../presence-contact-register/state/presence-contact-register.actions';
import { convertPhoneRegisterListItem } from '../../helpers/phones-helper';
import { PhonesFilterOption } from '../../models/phone-filter-option';
import { ExtensionPhoneOrderingOptions } from '../../models/phone-register-options';
import { PhoneRegisterListItem, PhoneRegisterResults } from '../../models/phone-register-results.model';
import { GetPhones } from '../../queries/generated/GetPhones';
import { PhoneRegisterApiService } from '../../services/phone-register-api.service';
import {
   changeSipUserPassword,
   changeSipUserPasswordFailure,
   changeSipUserPasswordSuccess,
   createExtensionPhone,
   deleteExtensionPhone,
   deleteExtensionPhoneSuccess,
   editExtensionPhone,
   extensionPhoneRegisterAdapterActions,
} from './extension-phone-register.actions';
import { selectEndCursor, selectOrderingOptions, selectPhones } from './extension-phone-register.reducer';

@Injectable()
export class ExtensionPhoneRegisterEffects {
   addPhone$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createExtensionPhone),
            withLatestFrom(this.store.pipe(select(selectRouteParams))),
            switchMap(([action, params]) => {
               const extensionId = parseInt(params.id, 10);
               const dialogRef = this.dialogService.openFormComponent(AddPhoneComponent, {
                  data: {
                     phoneType: action.createPhoneTypeOption.name,
                     extensionId: extensionId,
                     accountId: action.accountId,
                     accountName: action.accountShortName,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   changeSipUserPassword$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(changeSipUserPassword),
         switchMap((action) => {
            return this.extensionDetailApiService.changeSipUserPasswords([action.sipUserId]).pipe(
               map(() => {
                  return changeSipUserPasswordSuccess();
               }),
               catchError((error) => {
                  return of(changeSipUserPasswordFailure(error));
               })
            );
         })
      );
   });

   changePresenceContactInformation$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(PresenceContactRegisterActions.editPresenceContactSubmit),
         switchMap((action) => {
            return this.extensionDetailApiService
               .changeExtensionPresenceData(
                  action.dndStatus,
                  action.office,
                  action.extensionPhoneId,
                  action.extensionId,
                  action.departmentExtensions,
                  action.fields
               )
               .pipe(
                  map(() => {
                     this.snackbar.open(`Presence Contact Edited`);
                     return PresenceContactRegisterActions.editPresenceContactSuccess();
                  }),
                  catchError((error) => {
                     return of(PresenceContactRegisterActions.editPresenceContactFailure(error));
                  })
               );
         })
      );
   });

   editPhone$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(editExtensionPhone),
            switchMap((action) => {
               const dialogRef = this.dialogService.openFormComponent(AddPhoneComponent, {
                  data: {
                     accountId: action.accountId,
                     phoneId: action.phone.phoneId,
                     phoneType: action.phone.phoneType,
                     numberOfLineKeys: action.phone.numberOfLineKeys,
                     sipUsers: action.phone.sipUsers,
                     devices: action.phone.devices,
                     handsetIpei: action.phone.handsetIpei,
                     baseStationHandsets: action.phone.baseStationHandsets,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadPhoneList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            extensionPhoneRegisterAdapterActions.pageEnter,
            AddPhoneActions.savePhoneSuccess,
            ExtensionDetailActions.saveExtensionDetailSuccess,
            ExtensionDetailActions.getHackedFraudDetailsSuccess,
            addHardphoneSuccess,
            deleteExtensionPhoneSuccess
         ),
         withLatestFrom(
            this.store.pipe(select(selectRouteParams)),
            this.store.pipe(select(getHackedFraudDetails)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, params, hackedFraudDetails, orderingOptions]) => {
            return this.loadNewData(params, hackedFraudDetails, orderingOptions);
         })
      );
   });

   loadPhonePage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionPhoneRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(getHackedFraudDetails)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, endCursor, hackedFraudDetails, orderingOptions]) => {
            return this.phoneRegisterApiService.getPhones(endCursor, null, orderingOptions).pipe(
               map((result) => {
                  return extensionPhoneRegisterAdapterActions.loadPageSuccess(
                     this.convertGetPhonesResultToLoadSuccessProps(result.data, hackedFraudDetails)
                  );
               }),
               catchError((error) => {
                  return of(extensionPhoneRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   loadPhoneListLocally$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(AddPhoneActions.addPhoneToRegister, deleteExtensionPhone, changeSipUserPasswordSuccess),
         withLatestFrom(this.store.pipe(select(selectPhones)), this.store.pipe(select(selectEndCursor))),
         switchMap(([action, phones, endCursor]) => {
            const phoneRegisterResults: PhoneRegisterResults = {
               endCursor: endCursor,
               hasNextPage: false,
               items: phones,
               totalCount: phones.length,
            };
            return of(extensionPhoneRegisterAdapterActions.loadSuccess(phoneRegisterResults));
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(extensionPhoneRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectRouteParams)),
            this.store.pipe(select(getHackedFraudDetails)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectPhones))
         ),
         switchMap(([action, params, hackedFraudDetails, orderingOptions, phones]) => {
            return this.loadNewData(params, hackedFraudDetails, orderingOptions, phones);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private phoneRegisterApiService: PhoneRegisterApiService,
      private extensionDetailApiService: ExtensionDetailApiService,
      private dialogService: LcsDialogService,
      private snackbar: MatSnackBar
   ) {}

   private loadNewData(
      params: any = {},
      hackedFraudDetails: ExtensionHackedFraudDetails | null = null,
      orderingOptions: ExtensionPhoneOrderingOptions[] | null = null,
      existingPhones: PhoneRegisterListItem[] | null = null
   ) {
      const extensionId = parseInt(params.id, 10);
      const filterExpression = extensionId ? this.buildFilterExpression(extensionId) : null;
      return this.phoneRegisterApiService.getPhones(null, filterExpression, orderingOptions).pipe(
         map((result) => {
            let newlyAddedPhones: PhoneRegisterListItem[] = [];
            if (existingPhones && existingPhones.length > 0) {
               newlyAddedPhones = existingPhones.filter(
                  (phone) => !result.data.phonesConnection.items.some((item) => item.phoneId === phone.phoneId)
               );
            }
            if (newlyAddedPhones.length > 0) {
               return extensionPhoneRegisterAdapterActions.loadSuccess(
                  this.convertGetPhonesResultToLoadSuccessProps(result.data, hackedFraudDetails, newlyAddedPhones)
               );
            } else {
               return extensionPhoneRegisterAdapterActions.loadSuccess(
                  this.convertGetPhonesResultToLoadSuccessProps(result.data, hackedFraudDetails)
               );
            }
         }),
         catchError((error) => {
            return of(extensionPhoneRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private convertGetPhonesResultToLoadSuccessProps(
      getPhones: GetPhones,
      hackedFraudDetails: ExtensionHackedFraudDetails | null,
      newlyAddedPhones: PhoneRegisterListItem[] | null = null
   ): PhoneRegisterResults {
      let itemsToReturn: PhoneRegisterListItem[] = getPhones.phonesConnection.items.map((getPhoneItem) =>
         convertPhoneRegisterListItem(getPhoneItem, hackedFraudDetails)
      );
      if (newlyAddedPhones && newlyAddedPhones.length > 0) {
         const missingPhones = newlyAddedPhones.filter(
            (phone) => !getPhones.phonesConnection.items.some((item) => item.phoneId === phone.phoneId)
         );
         if (missingPhones.length > 0) {
            itemsToReturn.push(...missingPhones);
         }
      }
      return {
         endCursor: getPhones.phonesConnection.pageInfo.endCursor,
         hasNextPage: getPhones.phonesConnection.pageInfo.hasNextPage,
         items: itemsToReturn,
         totalCount: getPhones.phonesConnection.totalCount,
      };
   }

   private buildFilterExpression(extensionId: number): PhonesFilterOption[] {
      return [
         {
            field: 'extensionId',
            operation: FilterOperations.EqualTo,
            type: 'numeric',
            value: extensionId,
         },
      ];
   }
}
